import React, { useState, useEffect, useMemo } from 'react'
import { CONFIG_DEFAULT_STATE, LAYER_DEFAULT_STATE } from '../../constants'
import ActionToggle from 'live/components/ui/action-toggle'
import { ColumnContainer, TitleDiv, ToggleDetails, ToggleLabel } from '../ValueOptions'
import i18n from 'live/services/i18n'
import { InputType } from '../InputType'
import styled from 'styled-components'
import { MainModal } from '../MainModal'
import { LayerConfigInterface, StateConfigInterface } from '../../types'
import { ChartFields } from '../../redux/reducer'

type Props = {
    config: StateConfigInterface & { properties: Array<unknown> }
    onChange: (type: string, value: unknown) => void
    fields: ChartFields
}

const ConfigWidget = ({ config, onChange, fields }: Props): JSX.Element => {
    const { properties } = config
    const { valueFormat, layersConfig = {}, isSizeFixed } = config || CONFIG_DEFAULT_STATE
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
    const [layerSelected, setLayerSelected] = useState<{ key: string; layerConfig: LayerConfigInterface }>(null)
    const [newValueFormat, setNewValueFormat] = useState(valueFormat)

    useEffect(() => {
        normalizeLayers()
    }, [properties])

    const variableOptions = useMemo(() => {
        let newVariableOptions = {}
        Object.entries(fields).map(([layer, values]) => {
            newVariableOptions = {
                ...newVariableOptions,
                [layer]: []
            }
            values.forEach(value => {
                if (value.includes('__')) return
                newVariableOptions[layer].push({
                    label: value,
                    value: value
                })
            })
        })
        return newVariableOptions
    }, [fields])

    const normalizeLayers = (): void => {
        let normalizedLayers = { ...layersConfig }
        properties.forEach((property, index) => {
            normalizedLayers = {
                ...normalizedLayers,
                [0]: { ...normalizedLayers[0], name: `Layer 1` }
            }
            if (!normalizedLayers[index]) {
                normalizedLayers = {
                    ...normalizedLayers,
                    [index]: { ...LAYER_DEFAULT_STATE, name: `Layer ${index + 1}` }
                }
            }
        })
        if (Object.keys(normalizedLayers).length > properties.length) {
            for (let i = 0; i < Object.keys(normalizedLayers).length; i++) {
                if (!properties[i]) delete normalizedLayers[i]
            }
        }
        onChange('layersConfig', normalizedLayers)
    }

    const changeLayerConfig = (layerKey: string, newLayerConfig: LayerConfigInterface) => {
        const newLayersConfig = { ...layersConfig, [layerKey]: newLayerConfig }
        onChange('layersConfig', newLayersConfig)
    }

    const handleOpenConfigModal = () => {
        setIsConfigModalOpen(!isConfigModalOpen)
    }

    // const handleIsSizeFixed = () => {
    //     onChange('isSizeFixed', !isSizeFixed)
    // }

    const handleValueFormat = value => {
        setNewValueFormat(value)
        onChange('valueFormat', newValueFormat)
    }

    return (
        <>
            <Container>
                {/* TODO: rework for next version 
                
                <ColumnContainer>
                    <StyledTitle>{i18n('Content size').toUpperCase()}</StyledTitle>

                    <SubContainer>
                        <ActionToggle
                            active={isSizeFixed}
                            onChange={() => {
                                handleIsSizeFixed()
                            }}
                        />
                        <ToggleLabel>{i18n('Fixed Size')}</ToggleLabel>
                        <br />
                        <ToggleDetails>
                            {i18n(
                                'The content size will not follow the content and will be parametized through the defined minimum and maximum value.'
                            )}
                        </ToggleDetails>
                    </SubContainer>

                    <SubContainer>
                        <InputType
                            inputType="text"
                            textLabel="Number of Characters"
                            value={newValueFormat}
                            onChange={event => {
                                const value = event.target.value
                                handleValueFormat(value)
                            }}
                        />
                    </SubContainer>
                </ColumnContainer> */}

                <StyledColumnContainer>
                    <StyledTitle>{i18n('Content style').toUpperCase()}</StyledTitle>

                    <SubContainer>
                        <ToggleDetails>{i18n('Each layer has its own style configuration.')}</ToggleDetails>
                    </SubContainer>

                    {Object.entries(layersConfig).map(([key, layerConfig]) => (
                        <StyledLayerButton
                            key={key}
                            onClick={() => {
                                setLayerSelected({ key: key, layerConfig: layerConfig })
                                handleOpenConfigModal()
                            }}
                        >
                            <span>
                                <i className="fa fa-gear"></i>
                                {i18n('Style') + ' ' + i18n(layerConfig.name)}
                            </span>
                            <i className="fa fa-pencil"></i>
                        </StyledLayerButton>
                    ))}
                </StyledColumnContainer>
            </Container>

            {layerSelected && (
                <MainModal
                    layerKey={layerSelected?.key}
                    isVisible={isConfigModalOpen}
                    setIsVisible={handleOpenConfigModal}
                    layerConfig={layerSelected?.layerConfig}
                    changeLayerConfig={changeLayerConfig}
                    setLayerSelected={setLayerSelected}
                    variableOptions={variableOptions[layerSelected?.key]}
                />
            )}
        </>
    )
}

export { ConfigWidget }
export const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`

export const StyledColumnContainer = styled(ColumnContainer)`
    padding: 4px 0px 8px 0px;
`

export const SubContainer = styled.div`
    width: 100%;
    padding: 4px 0px;
    span {
        font-size: 11px;
    }
`

export const StyledTitle = styled(TitleDiv)`
    font-size: 13px;
    width: 100%;
    margin: 8px 0px;
    padding: 8px 0px;
`

export const StyledLayerButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 4px 0px;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme?.['gray-400'] || '#C8CCD4'};
    border-radius: 3px;
    color: ${({ theme }) => theme?.['gray-600'] || '#868695'};
    background-color: ${({ theme }) => theme?.['gray-200'] || '#EEEEEE'};

    span {
        font-size: 11px;
        font-weight: 600;

        i {
            padding-right: 8px;
        }
    }
`
